
// React core imports
import React, { useMemo } from 'react';

// Context utilities
import { AiAssistantContext, createContextValue } from './ai-assistant/AiAssistantContextUtils';
import { useAiAssistantProviderDependencies } from './ai-assistant/AiAssistantProviderDependencies';
import { providerConfig, ProviderFallbackUI } from './ai-assistant/AiAssistantProviderConfig';

// Export configurations for backwards compatibility
export { TOPIC_CONFIG, CATEGORY_CONFIG } from '@/config/ai-assistant-topics';

// Export the useAiAssistant hook
export { useAiAssistant } from './ai-assistant/AiAssistantContextUtils';

/**
 * AiAssistantProvider - Main context provider for AI Assistant functionality
 * 
 * This provider orchestrates all AI Assistant functionality by:
 * - Managing dependencies through useAiAssistantProviderDependencies
 * - Creating optimized context values
 * - Providing error boundaries and fallback UI
 */
export const AiAssistantProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  try {
    // Get all dependencies through the dedicated hook
    const dependencies = useAiAssistantProviderDependencies();

    // Validate dependencies
    if (!dependencies) {
      throw new Error('Dependencies hook returned undefined');
    }

    // Create optimized context value using useMemo
    const contextValue = useMemo(() => {
      const value = createContextValue(dependencies);
      return value;
    }, [dependencies]);

    // Validate context value
    if (!contextValue) {
      throw new Error('Context value is undefined');
    }

    return (
      <AiAssistantContext.Provider value={contextValue}>
        {children}
      </AiAssistantContext.Provider>
    );

  } catch (error) {
    providerConfig.logError(
      error instanceof Error ? error : new Error(String(error)),
      'initialization failed'
    );

    // Fallback UI for initialization errors
    return <ProviderFallbackUI />;
  }
};
